/**
 * Patient-facing wait/tracking page
 * Route: /clinic/wait?token=XXXXXXXX
 * 
 * This is a standalone page patients open by scanning their QR code.
 * No auth required. Auto-refreshes every 8s. Fires alarm when turn comes.
 * 
 * Add to your router:
 *   import ClinicWaitPage from './components/ClinicWaitPage';
 *   <Route path="/clinic/wait" element={<ClinicWaitPage />} />
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface TokenStatus {
  token_number:        number;
  token_code:          string;
  patient_name:        string;
  status:              string;
  doctor?:             string;
  department?:         string;
  appointment_date?:   string;
  appointment_time?:   string;
  reason?:             string;
  current_number:      number;
  tokens_ahead:        number;
  estimated_wait_mins: number;
  clinic_name?:        string;
  doctor_name?:        string;
  is_open:             boolean;
  called_at?:          string;
  is_your_turn:        boolean;
}

export default function ClinicWaitPage() {
  const tokenCode = new URLSearchParams(window.location.search).get('token') || '';
  const [data, setData] = useState<TokenStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const alarmFired = useRef(false);
  const intervalRef = useRef<any>(null);
  const prevStatus = useRef<string>('');

  const fireAlarm = useCallback((d: TokenStatus) => {
    if (alarmFired.current) return;
    alarmFired.current = true;

    // Vibrate
    if ('vibrate' in navigator) {
      navigator.vibrate([600, 200, 600, 200, 600, 200, 600]);
    }

    // Audio beeps
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const beep = (t: number, freq: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'triangle';
        gain.gain.setValueAtTime(0.5, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
        osc.start(t);
        osc.stop(t + dur);
      };
      beep(ctx.currentTime,       880, 0.4);
      beep(ctx.currentTime + 0.5, 1100, 0.4);
      beep(ctx.currentTime + 1.0, 880, 0.4);
      beep(ctx.currentTime + 1.5, 1100, 0.6);
    } catch {}

    // Push notification
    if ('Notification' in window) {
      const send = () => new Notification(`🔔 Your Turn! Token #${d.token_number}`, {
        body: `Please proceed to ${d.doctor || d.clinic_name || 'the doctor'} now.`,
        icon: '/favicon.ico',
        tag: 'clinic-turn',
        requireInteraction: true,
      });
      if (Notification.permission === 'granted') {
        send();
      } else {
        Notification.requestPermission().then(p => { if (p === 'granted') send(); });
      }
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!tokenCode) { setError('No token provided.'); setLoading(false); return; }
    try {
      const r = await fetch(`${BASE}/clinic/tokens/${tokenCode}/status`);
      if (!r.ok) { setError('Token not found.'); setLoading(false); return; }
      const d: TokenStatus = await r.json();
      setData(d);
      setLastUpdated(new Date());

      if (d.is_your_turn && prevStatus.current !== 'called') {
        fireAlarm(d);
      }
      prevStatus.current = d.status;
    } catch {
      setError('Could not connect to server.');
    }
    setLoading(false);
  }, [tokenCode, fireAlarm]);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 8000);
    // Ask for notification permission early
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    return () => clearInterval(intervalRef.current);
  }, [fetchStatus]);

  if (!tokenCode) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: 'center', padding: '80px 32px', color: '#dc2626' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>❌</div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>Invalid Link</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Please scan your appointment QR code again.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ ...pageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6366f1' }}>
          <div style={{ width: 48, height: 48, border: '4px solid #e0e7ff', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>Loading your token status...</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: 'center', padding: '80px 32px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>❓</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{error || 'Token not found'}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Please scan your appointment QR again or contact the front desk.</div>
        </div>
      </div>
    );
  }

  const isYourTurn = data.is_your_turn;
  const isDone     = data.status === 'done';
  const isCancelled = data.status === 'cancelled';
  const isWaiting  = ['waiting', 'hold', 'urgent'].includes(data.status);

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{
        background: isYourTurn ? 'linear-gradient(135deg,#15803d,#16a34a)' : 'linear-gradient(135deg,#0f172a,#1e293b)',
        padding: '20px 24px', textAlign: 'center', flexShrink: 0,
        transition: 'background 0.5s',
      }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'rgba(255,255,255,0.9)' }}>
          🏥 {data.clinic_name || 'Clinic Queue'}
        </div>
        {data.doctor_name && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
            Dr. {data.doctor_name}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, maxWidth: 420, margin: '0 auto', width: '100%' }}>

        {/* Big token ring */}
        <div style={{
          width: 180, height: 180, borderRadius: '50%',
          background: isYourTurn  ? 'linear-gradient(135deg,#16a34a,#15803d)'
            : isDone     ? '#f1f5f9'
            : isCancelled ? '#fef2f2'
            : 'linear-gradient(135deg,#6366f1,#4f46e5)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          boxShadow: isYourTurn
            ? '0 0 0 16px rgba(22,163,74,0.15), 0 0 0 32px rgba(22,163,74,0.07), 0 12px 40px rgba(22,163,74,0.4)'
            : '0 12px 40px rgba(99,102,241,0.3)',
          animation: isYourTurn ? 'pulse 1.2s ease-in-out infinite' : 'none',
          transition: 'all 0.5s',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {isDone ? 'Done' : isCancelled ? 'Cancelled' : 'Token'}
          </div>
          <div style={{ fontSize: 60, fontWeight: 900, color: isDone || isCancelled ? '#94a3b8' : '#fff', lineHeight: 1, letterSpacing: 3 }}>
            {String(data.token_number).padStart(3, '0')}
          </div>
          {isYourTurn && (
            <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', marginTop: 2, animation: 'fadeInUp 0.4s ease' }}>
              YOUR TURN! 🔔
            </div>
          )}
        </div>

        {/* Main status card */}
        <div style={{
          width: '100%', borderRadius: 16, padding: '20px 24px', textAlign: 'center',
          background: isYourTurn  ? '#f0fdf4'
            : isDone     ? '#f8fafc'
            : isCancelled ? '#fef2f2'
            : '#fffbeb',
          border: `2px solid ${isYourTurn ? '#bbf7d0' : isDone ? '#e2e8f0' : isCancelled ? '#fecaca' : '#fde68a'}`,
          transition: 'all 0.4s',
        }}>
          {isYourTurn ? (
            <>
              <div style={{ fontSize: 44, marginBottom: 8 }}>🔔</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#15803d', marginBottom: 6 }}>It's your turn!</div>
              <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
                Please proceed to <strong>{data.doctor || 'the doctor'}</strong> now.
              </div>
            </>
          ) : isDone ? (
            <>
              <div style={{ fontSize: 44, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#64748b' }}>Visit Completed</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>Thank you for your visit!</div>
            </>
          ) : isCancelled ? (
            <>
              <div style={{ fontSize: 44, marginBottom: 8 }}>❌</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#dc2626' }}>Appointment Cancelled</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Please contact the front desk.</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                Currently Serving
              </div>
              <div style={{ fontSize: 56, fontWeight: 900, color: '#0f172a', letterSpacing: 6, lineHeight: 1 }}>
                {String(data.current_number || 0).padStart(3, '0')}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 10, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                {data.tokens_ahead > 0 ? (
                  <>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{data.tokens_ahead} ahead of you</span>
                    <span>·</span>
                    <span>~{data.estimated_wait_mins} min wait</span>
                  </>
                ) : (
                  <span style={{ fontWeight: 800, color: '#16a34a', fontSize: 15 }}>🎉 You're next!</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Appointment details */}
        <div style={{ width: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', background: '#fafafa', borderBottom: '1px solid #f1f5f9', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Appointment Details
          </div>
          {[
            { label: 'Patient',    val: data.patient_name },
            { label: 'Doctor',     val: data.doctor },
            { label: 'Department', val: data.department },
            { label: 'Date',       val: data.appointment_date },
            { label: 'Time',       val: data.appointment_time },
            { label: 'Reason',     val: data.reason },
          ].filter(r => r.val).map((row, i, arr) => (
            <div key={row.label} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '11px 16px', borderBottom: i < arr.length - 1 ? '1px solid #f8fafc' : 'none',
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{row.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'right', maxWidth: '60%' }}>{row.val}</span>
            </div>
          ))}
        </div>

        {/* Notification prompt */}
        {'Notification' in window && Notification.permission === 'default' && isWaiting && (
          <button onClick={() => Notification.requestPermission()} style={{
            width: '100%', padding: '12px', borderRadius: 12,
            border: '2px dashed #6366f1', background: '#eef2ff',
            color: '#4f46e5', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            🔔 Enable notifications — get alerted when it's your turn
          </button>
        )}

        {/* Auto-refresh indicator */}
        <div style={{ fontSize: 11, color: '#cbd5e1', textAlign: 'center', lineHeight: 1.5 }}>
          {lastUpdated && `Last updated: ${lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
          <br />Auto-refreshes every 8 seconds · Keep this page open
        </div>

      </div>

      <style>{`
        @keyframes spin     { to { transform: rotate(360deg); } }
        @keyframes pulse    { 0%,100%{box-shadow:0 0 0 16px rgba(22,163,74,0.15),0 0 0 32px rgba(22,163,74,0.07),0 12px 40px rgba(22,163,74,0.4)} 50%{box-shadow:0 0 0 24px rgba(22,163,74,0.08),0 0 0 44px rgba(22,163,74,0.03),0 12px 40px rgba(22,163,74,0.5)} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        * { -webkit-tap-highlight-color: transparent; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  background: '#f8fafc',
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};
